'use client';

import React, { useEffect, useRef, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CtaFinal from '../homepage/components/CtaFinal';


function useReveal(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = ref?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [ref]);
}

function Calculator() {
  const [reps, setReps] = useState(1);

  const cvCost = 3499 * reps;
  const ihCost = 10000 * reps;
  const yearly = (ihCost - cvCost) * 12;

  const fmt = (n: number) => '$' + Math.round(n).toLocaleString('en-US');

  return (
    <div
      className="rev rounded-2xl p-10 text-cream border border-line-navy"
      style={{ background: 'var(--navy-deep)' }}
    >
      <label className="font-mono text-[12px] tracking-[0.16em] uppercase text-muted-cream block mb-2.5">
        Dedicated reps on your team:{' '}
        <span className="text-primary font-semibold">{reps}</span>
      </label>
      <input
        type="range"
        min={1}
        max={6}
        value={reps}
        onChange={(e) => setReps(Number(e.target.value))}
        className="w-full mb-7"
        style={{ accentColor: 'var(--gold)' }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <div className="bg-navy-panel border border-line-navy rounded-xl p-5">
          <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-muted-cream block mb-2">
            CommandVA monthly cost
          </span>
          <b className="font-display text-[34px] text-primary font-bold">{fmt(cvCost)}</b>
          <i className="block text-[12.5px] text-muted-cream font-normal mt-1.5">
            Reps, dialer, data, management, and reporting included
          </i>
        </div>
        <div className="bg-navy-panel border border-line-navy rounded-xl p-5">
          <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-muted-cream block mb-2">
            In-house equivalent
          </span>
          <b className="font-display text-[34px] text-white font-bold">roughly {fmt(ihCost)}</b>
          <i className="block text-[12.5px] text-muted-cream font-normal mt-1.5">
            Fully loaded, before turnover and recruiting
          </i>
        </div>
      </div>
      <div className="bg-primary text-navy-deep rounded-xl p-4 font-semibold text-center text-[17px]">
        You save roughly {fmt(yearly)} per year against in-house hires
      </div>
      <p className="font-mono text-[11.5px] text-muted-cream mt-4">
        In-house figure uses the roughly $10,000 per month fully loaded benchmark per seat. Qualified-meeting fees are set in your agreement and not included here. Estimates for planning, not a quote.
      </p>
    </div>
  );
}

export default function MathPage() {
  const sec1 = useRef<HTMLDivElement>(null);
  const sec2 = useRef<HTMLDivElement>(null);
  const sec3 = useRef<HTMLDivElement>(null);
  const sec4 = useRef<HTMLDivElement>(null);
  useReveal(sec1);
  useReveal(sec2);
  useReveal(sec3);
  useReveal(sec4);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <header
          className="py-24"
          style={{
            background:
              'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
          }}
        >
          <div className="max-w-site mx-auto px-7">
            <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
              Reasoning, not marketing
            </div>
            <h1 className="heading-1 text-white">The math</h1>
            <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[62ch]">
              Every claim on this site reduces to one comparison: what a dedicated outbound seat truly costs in-house against what it costs here. Here is the full working.
            </p>
          </div>
        </header>

        {/* Part 1 */}
        <section ref={sec1} className="py-24">
          <div className="max-w-site mx-auto px-7">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="rev">
                <div className="eyebrow">Part one</div>
                <h2 className="heading-2 text-ink" style={{ fontSize: 'clamp(30px,3.4vw,42px)' }}>
                  What an in-house SDR really costs
                </h2>
                <p className="mt-4 text-[19px] text-muted-text leading-relaxed">
                  Salary is less than two thirds of the bill. The rest hides in benefits, tooling, management time, and the ramp period where you pay full price for partial output.
                </p>
                <p className="mt-4 text-muted-text">
                  Then turnover multiplies it. Median SDR tenure is under two years, one in five new SDRs quits within 90 days, and each departure restarts the ramp clock. Modeled over three years, replacement costs add $52K to $99K per seat per year on top of everything in the table.
                </p>
              </div>
              <div className="rev bg-white border border-line-color rounded-brand overflow-hidden" style={{ transitionDelay: '0.1s' }}>
                {[
                  { label: 'Base salary (median)', val: '$4,600 /mo' },
                  { label: 'Variable compensation', val: '$1,700 /mo' },
                  { label: 'Benefits and payroll taxes', val: '$1,600 /mo' },
                  { label: 'Data, tools, and phone stack', val: '$800 /mo' },
                  { label: 'Management time (5 hrs/week)', val: '$900 /mo' },
                  { label: 'Ramp amortization (4 to 6 months)', val: '$700 /mo' },
                ].map((row) => (
                  <div key={row.label} className="stack-row">
                    <span className="text-muted-text">{row.label}</span>
                    <b className="font-mono font-semibold">{row.val}</b>
                  </div>
                ))}
                <div className="stack-row bg-navy text-cream">
                  <span className="text-cream">Fully loaded, before turnover</span>
                  <b className="font-mono text-primary">roughly $10,000 /mo</b>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Part 2 */}
        <section ref={sec2} className="py-24 bg-white">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-13 max-w-[780px]">
              <div className="eyebrow">Part two</div>
              <h2 className="heading-2 text-ink">The same seat at CommandVA</h2>
              <p className="mt-4 text-[19px] text-muted-text leading-relaxed">
                A dedicated CommandVA rep is one flat monthly seat, published on the pricing page. The dialer, verified calling data, supporting email and LinkedIn touches, management, and reporting are inside that number, and the ramp risk is ours.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { val: '65%', desc: 'Lower monthly cost per seat than the fully loaded in-house benchmark.' },
                { val: '$78K+', desc: 'Annual savings per seat against one in-house hire, before counting turnover risk.' },
                { val: '$0', desc: 'Recruiting fees, severance events, ramp payroll, or tool stack purchases.' },
                { val: '30 days', desc: 'Notice to cancel. The employment-law and commitment risk of a hire never enters the picture.' },
              ].map((s, i) => (
                <div key={s.val} className="rev stat-card bg-white border border-line-color rounded-brand p-6" style={{ transitionDelay: `${i * 0.08}s` }}>
                  <b>{s.val}</b>
                  <span>{s.desc}</span>
                </div>
              ))}
            </div>
            <div className="rev gold-strip mt-8" style={{ transitionDelay: '0.32s' }}>
              <div>
                <b className="text-primary">The statistic that settles it:</b> US SDR turnover runs 34 to 40% per year with median tenure under two years. When our rep needs replacing someday, the recruiting, training, and transition are our cost. When yours does, it is $78K or more of yours.
              </div>
            </div>
          </div>
        </section>

        {/* Part 3 - Calculator */}
        <section ref={sec3} className="py-24">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-13 max-w-[780px]">
              <div className="eyebrow">Part three</div>
              <h2 className="heading-2 text-ink">Run your own numbers</h2>
            </div>
            <Calculator />
          </div>
        </section>

        {/* Part 4 */}
        <section ref={sec4} className="py-16 bg-white">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-13 max-w-[780px]">
              <div className="eyebrow">Part four</div>
              <h2 className="heading-2 text-ink">Why the channel data favors phone-first</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  tag: 'Phone',
                  title: 'Calling quietly recovered',
                  body: 'Cold call success rates rose in 2026 as reps abandoned the channel for automation. Decision-makers hear from fewer callers than they have in years, and a prepared caller who reaches one books the meeting most of the time. Voice is where our reps live.',
                },
                {
                  tag: 'Email',
                  title: 'Inboxes got harder',
                  body: 'Average cold email reply rates dropped from 8.5% in 2019 to under 4% in 2026 as inboxes filled with automated volume. Email still works as a supporting touch on small, targeted lists, which is exactly how we use it: to warm the call and follow the conversation.',
                },
                {
                  tag: 'Automation',
                  title: 'AI alone underperformed',
                  body: 'Fully autonomous AI SDR tools churned 50 to 70% of customers within a year. The model that works is the one we run: software finds the signals, a trained human picks up the phone.',
                },
              ].map((card, i) => (
                <div key={card.tag} className="rev bg-white border border-line-color rounded-brand p-7" style={{ transitionDelay: `${i * 0.09}s` }}>
                  <span className="block font-mono text-[11px] tracking-[0.18em] text-primary uppercase mb-3.5">{card.tag}</span>
                  <h3 className="heading-3 text-ink mb-3">{card.title}</h3>
                  <p className="text-[15px] text-muted-text leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>
            <div className="rev gold-strip mt-8" style={{ transitionDelay: '0.27s' }}>
              <div>
                <b className="text-primary">The multiplier:</b> calling and emailing the same account together roughly doubles reply rates compared to email alone, even when the call does not connect. That is why every CommandVA cadence is phone-anchored with email and LinkedIn in support.
              </div>
            </div>
          </div>
        </section>

        <CtaFinal
          headline="Bring this math to a call"
          body="We will model your deal size, close rate, and team plan live, and tell you what payback actually looks like."
          cta="Book a strategy call"
        />
      </main>
      <Footer />
    </>
  );
}
